import { useSetState } from '@/shared/lib/customHooks';
import { useEffect } from 'react';

const BACKGROUND_LENGTH = 2;

/**
 * Drives the rotating marketing banner and the Learn More modals it opens. Records which banner content was clicked and opens the CIED management modal for that banner or the generic Learn More modal otherwise, adds the have-banner class to scrollable rows on mount, and advances the background index every ten seconds, wrapping at the background count.
 *
 * @returns The banner state, the two modal togglers and the content click handler.
 */
export const useMarketingBanner = () => {
  const [state, setState] = useSetState({
    backgroundIndex: 0,
    isOpenLearnMoreModal: false,
    isOpenLearnMoreUniversalCiedManagementModal: false,
    isClickedUniversalCiedManagementBannder: false,
    isClickedBiofluxTradeInBanner: false,
    isClickedRpmCcmServiceBanner: false,
  });

  /**
   * Opens or closes the Learn More modal of the universal CIED management banner. Flips state.isOpenLearnMoreUniversalCiedManagementModal.
   *
   * @returns Nothing.
   */
  const toggleLearnMoreUniversalCiedManagementModal = () => {
    setState({
      isOpenLearnMoreUniversalCiedManagementModal:
        !state.isOpenLearnMoreUniversalCiedManagementModal,
    });
  };

  /**
   * Opens or closes the marketing banner's Learn More modal. Flips state.isOpenLearnMoreModal.
   *
   * @returns Nothing.
   */
  const toggleLearnMoreModal = () => {
    setState({ isOpenLearnMoreModal: !state.isOpenLearnMoreModal });
  };

  /**
   * Opens the Learn More modal matching the marketing banner that was clicked.
   * Records which of the CIED management, Bioflux Trade In or RPM/CCM banners was clicked, then
   * toggles the CIED management modal for that banner and the generic Learn More modal otherwise.
   *
   * @param props - Flags naming the clicked banner, each defaulting to false.
   * @returns Nothing.
   */
  const onClickContent = ({
    isClickedUniversalCiedManagementBannder = false,
    isClickedBiofluxTradeInBanner = false,
    isClickedRpmCcmServiceBanner = false,
  }) => {
    setState({
      isClickedUniversalCiedManagementBannder,
      isClickedBiofluxTradeInBanner,
      isClickedRpmCcmServiceBanner,
    });
    if (isClickedUniversalCiedManagementBannder) {
      toggleLearnMoreUniversalCiedManagementModal();
    } else {
      toggleLearnMoreModal();
    }
  };

  useEffect(() => {
    const rowEls = document.getElementsByClassName('flexible-scroll');
    if (rowEls.length > 0) {
      for (let i = 0; i < rowEls.length; i += 1) {
        rowEls[i].classList.add('--have-banner');
      }
    }
  }, []);

  useEffect(() => {
    /**
     * Sets up the 10-second interval that advances the marketing banner's background slide. Increments backgroundIndex, wrapping back to 0 after the last slide.
     *
     * @returns The interval timer id.
     */
    const backgroundInterval = setInterval(() => {
      setState((prevState) => {
        if (prevState.backgroundIndex < BACKGROUND_LENGTH - 1) {
          return { backgroundIndex: prevState.backgroundIndex + 1 };
        }
        return { backgroundIndex: 0 };
      });
    }, 10000);
    return () => {
      clearInterval(backgroundInterval);
    };
  }, []);

  return {
    state,
    toggleLearnMoreUniversalCiedManagementModal,
    toggleLearnMoreModal,
    onClickContent,
  };
};
